<template>
  <div class="px-8 py-8 bg-white">
    <!-- Header -->
    <div class="flex items-center justify-between mb-12">
      <h1 class="text-[28px]  font-semibold text-gray-900">User Engagement</h1>

      <button
        type="button"
        class="auth-btn "
        @click="goAuth"
      >
        Authentication
      </button>
    </div>

    <!-- =================== TOP SECTION (same style as dashboard) =================== -->
    <div class="grid grid-cols-12 gap-6">
      <!-- Top Left: User Activity Over time -->
      <div class="col-span-12 lg:col-span-8 card h-[300px]">
        <div class="flex items-start justify-between">
          <h2 class="card-title">User Activity Over time</h2>
        </div>

        <div class="mt-4">
          <ApexChart
            type="line"
            height="220"
            :options="activityOptions"
            :series="activitySeries"
          />
        </div>
      </div>

      <!-- Top Right: 2 cards -->
      <div class="col-span-12 lg:col-span-4 flex flex-col gap-6 h-[300px]">
        <!-- Card 1 -->
        <div class="card ">
          <h2 class="card-title">Active users in last 30 minutes</h2>

          <div class="mt-3 flex items-center justify-between">
            <div class="text-[26px] font-semibold text-gray-800">300</div>

            <div class="flex items-center gap-2 text-[14px] text-gray-500">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 17L9 11L13 15L21 7"
                  stroke="#22C55E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M21 7H17"
                  stroke="#22C55E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>4.5%</span>
            </div>
          </div>
        </div>

        <!-- Card 2 -->
        <div class="card ">
          <div class="flex items-center justify-between">
            <h3 class="card-subtitle">Top Countries</h3>
            <span class="text-[12px] font-medium text-green-500">Active Users</span>
          </div>

          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <span class="row-left">Jordan</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">Qatar</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">UAE</span>
              <span class="row-right">000</span>
            </div>
          </div>
        </div>
      </div>

      <!-- =================== MIDDLE SECTION =================== -->
      <!-- Middle Left: 2 cards stacked -->
      <div class="col-span-12 lg:col-span-4 flex flex-col gap-6">
        <!-- Active Users (Currently Version) -->
        <div class="card">
          <div class="flex items-center ">
            <div class="flex items-center gap-2">
              <div class="text-[18px] font-semibold text-gray-800">Active Users</div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 10l5 5 5-5"
                  stroke="#6B7280"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>

            <div class="text-[15px] font-bold text-gray-700">Currently Version</div>
          </div>

          <div class="mt-6 flex items-center justify-between">
            <div class="text-[28px] font-semibold text-gray-800">300</div>

            <div class="flex items-center gap-2 text-[14px] text-gray-500">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 17L9 11L13 15L21 7"
                  stroke="#22C55E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M21 7H17"
                  stroke="#22C55E"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span>4.5%</span>
            </div>
          </div>
        </div>

        <!-- Key event by event name (small) -->
        <div class="card">
          <div class="text-[18px] font-semibold text-gray-700">
            Key event by event name
          </div>

          <div class="mt-3 flex items-center justify-between text-[12px] text-gray-500">
            <span>Event Name</span>
            <span>Key Event</span>
          </div>

          <div class="mt-4 space-y-4">
            <div class="flex items-center justify-between">
              <span class="row-left">Jordan</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">Qatar</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">UAE</span>
              <span class="row-right">000</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Middle Right: Active Users By Version chart -->
      <div class="col-span-12 lg:col-span-8 card">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="text-[18px] font-semibold text-gray-800">Active Users</div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 10l5 5 5-5"
                stroke="#6B7280"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="text-[14px] font-medium text-gray-700">By Version</div>
        </div>

        <div class="mt-4">
          <ApexChart
            type="line"
            height="240"
            :options="versionOptions"
            :series="versionSeries"
          />
        </div>
      </div>

      <!-- =================== LOWER SECTION (as in second screenshot) =================== -->
      <!-- Engagement time chart (left) + metrics (right) -->
      <div class="col-span-12 lg:col-span-8 card">
        <div class="text-[18px] font-semibold text-gray-800">Engagement</div>

        <div class="mt-4">
          <ApexChart
            type="line"
            height="220"
            :options="engagementOptions"
            :series="engagementSeries"
          />
        </div>
      </div>

      <div class="col-span-12 lg:col-span-4 card">
        <div class="metric-block">
          <div class="metric-title">Average engagement time per active user</div>
          <div class="metric-value">3 m 50 s</div>
        </div>

        <div class="metric-block">
          <div class="metric-title">Engaged session per active user</div>
          <div class="metric-value">1 m 50 s</div>
        </div>

        <div class="metric-block">
          <div class="metric-title">Average engagement time per active Session</div>
          <div class="metric-value">2 m 33 s</div>
        </div>
      </div>

      <!-- Retention (left) + key events (right) -->
      <div class="col-span-12 lg:col-span-6 card">
        <div class="text-[18px] font-semibold text-gray-800">User Retention</div>

        <div class="mt-4">
          <ApexChart
            type="line"
            height="220"
            :options="retentionOptions"
            :series="retentionSeries"
          />
        </div>
      </div>

      <div class="col-span-12 lg:col-span-6 card">
        <div class="text-[18px] font-semibold text-gray-800">
          Key event by event name
        </div>

        <div class="mt-3 flex items-center justify-between text-[12px] text-gray-500">
          <span>Event name</span>
          <span>Event count</span>
        </div>

        <div class="mt-4 space-y-4">
          <div class="flex items-center justify-between">
            <span class="row-left">User Engagement</span>
            <span class="row-right">000</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Screen View</span>
            <span class="row-right">000</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Session start</span>
            <span class="row-right">000</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">First app open</span>
            <span class="row-right">000</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">App Remove</span>
            <span class="row-right">000</span>
          </div>
          <div class="flex items-center justify-between">
            <span class="row-left">Os update</span>
            <span class="row-right">000</span>
          </div>

          <div class="pt-2 flex justify-end">
            <button class="view-events">view Events</button>
          </div>
        </div>
      </div>

      <!-- Active users by country (full width) -->
      <div class="col-span-12 card">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="text-[18px] font-semibold text-gray-800">Active Users</div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 10l5 5 5-5"
                stroke="#6B7280"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <div class="text-[14px] font-medium text-gray-700">By Country</div>
        </div>

        <div class="mt-4">
          <div class="flex items-center justify-between text-[12px] text-gray-500">
            <span>Country</span>
            <span>Active users</span>
          </div>

          <div class="mt-4 space-y-4">
            <div class="flex items-center justify-between">
              <span class="row-left">Jordan</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">Screen View</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">Saudi Arabia</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">UAE</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">USA</span>
              <span class="row-right">000</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="row-left">Qatar</span>
              <span class="row-right">000</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import ApexChart from "vue3-apexcharts";

const router = useRouter();
const goAuth = () => router.push("/authentication");

/* ---------- Shared categories (7 points) ---------- */
const dateCategories = ["30\nNov", "03\nDec", "07\nDec", "10\nDec", "14\nDec", "17\nDec", "21\nDec"];

/* ---------- Top left chart: activity ---------- */
const activitySeries = [
  { name: "30Day", data: [220, 240, 210, 235, 230, 310, 350] },
  { name: "07Day", data: [100, 140, 110, 130, 165, 180, 210] },
  { name: "01Day", data: [50, 70, 60, 75, 105, 100, 135] },
];

const activityOptions = {
  chart: { toolbar: { show: false }, zoom: { enabled: false } },
  stroke: { curve: "smooth", width: 3 },
  colors: ["#8B1A9C", "#F59E0B", "#22C55E"],
  grid: {
    borderColor: "rgba(107,114,128,0.35)",
    strokeDashArray: 0,
    padding: { right: 40, bottom: 18 },
  },
  markers: { size: 0 },
  dataLabels: { enabled: false },
  legend: {
    position: "right",
    horizontalAlign: "left",
    fontSize: "12px",
    labels: { colors: ["#6B7280"] },
    markers: { width: 10, height: 10, radius: 10 },
    itemMargin: { vertical: 10 },
  },
  xaxis: {
    categories: dateCategories,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      offsetY: 6,
      whiteSpace: "pre",
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    min: 0,
    max: 400,
    tickAmount: 4,
    labels: { style: { colors: "#111827", fontSize: "10px" } },
  },
  tooltip: { enabled: true },
};

/* ---------- By Version chart (middle right) ---------- */
const versionSeries = [
  { name: "1.7.0", data: [150, 210, 205, 300, 245, 260, 340] },
  { name: "1.7.2", data: [95, 140, 135, 200, 190, 210, 295] },
  { name: "1.6.9", data: [30, 80, 75, 135, 120, 150, 235] },
  { name: "1.5.6", data: [10, 40, 35, 95, 70, 95, 180] },
];

const versionOptions = {
  chart: { toolbar: { show: false }, zoom: { enabled: false } },
  stroke: { curve: "smooth", width: 3 },
  colors: ["#F4B400", "#22C55E", "#FF2D55", "#1976FF"],
  grid: {
    borderColor: "rgba(107,114,128,0.35)",
    strokeDashArray: 0,
    padding: { right: 40, bottom: 18 },
  },
  markers: { size: 0 },
  dataLabels: { enabled: false },
  legend: {
    position: "right",
    horizontalAlign: "left",
    fontSize: "12px",
    labels: { colors: ["#6B7280"] },
    markers: { width: 10, height: 10, radius: 10 },
    itemMargin: { vertical: 8 },
  },
  xaxis: {
    categories: dateCategories,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      offsetY: 6,
      whiteSpace: "pre",
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    min: 0,
    max: 400,
    tickAmount: 4,
    labels: { style: { colors: "#111827", fontSize: "10px" } },
  },
  tooltip: { enabled: true },
};

/* ---------- Engagement chart (lower section) ---------- */
const engagementSeries = [
  { name: "Avg time", data: [60, 210, 85, 165, 240, 110, 220] }, // seconds-like values (visual only)
];

const engagementOptions = {
  chart: { toolbar: { show: false }, zoom: { enabled: false } },
  stroke: { curve: "smooth", width: 3 },
  colors: ["#2563EB"],
  grid: {
    borderColor: "rgba(107,114,128,0.35)",
    strokeDashArray: 0,
    padding: { right: 40, bottom: 18 },
  },
  markers: { size: 0 },
  dataLabels: { enabled: false },
  legend: { show: false },
  xaxis: {
    categories: dateCategories,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      offsetY: 6,
      whiteSpace: "pre",
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    min: 0,
    max: 260,
    tickAmount: 4,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      formatter: (v) => {
        // show as "m s" style (for look only)
        const total = Math.round(v);
        const m = Math.floor(total / 60);
        const s = total % 60;
        return `${m} m ${s} s`;
      },
    },
  },
  tooltip: { enabled: true },
};

/* ---------- Retention chart ---------- */
const retentionSeries = [
  { name: "Retention", data: [100, 45, 30, 80, 15, 25, 28] },
];

const retentionOptions = {
  chart: { toolbar: { show: false }, zoom: { enabled: false } },
  stroke: { curve: "smooth", width: 3 },
  colors: ["#22C55E"],
  grid: {
    borderColor: "rgba(107,114,128,0.35)",
    strokeDashArray: 0,
    padding: { right: 40, bottom: 18 },
  },
  markers: { size: 0 },
  dataLabels: { enabled: false },
  legend: { show: false },
  xaxis: {
    categories: dateCategories,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      offsetY: 6,
      whiteSpace: "pre",
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    min: 0,
    max: 100,
    tickAmount: 4,
    labels: {
      style: { colors: "#111827", fontSize: "10px" },
      formatter: (v) => `${Math.round(v)}.0 %`,
    },
  },
  tooltip: { enabled: true },
};
</script>

<style scoped>
.card {
  background: #f9f9f9;
  border-radius: 20px;
  padding: 20px 30px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.06), 0 10px 20px rgba(0, 0, 0, 0.08);
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.card-subtitle {
  font-size: 22px;
  font-weight: 600;
  color: #374151;
}

.row-left {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}

.row-right {
  font-size: 14px;
  color: #6b7280;
  letter-spacing: 2px;
}

.auth-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 999px;
  background: #5b9ac5;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}
.auth-btn:hover {
  opacity: 0.95;
}

.metric-block {
  background: #ececec;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 14px;
}
.metric-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
}
.metric-value {
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.view-events {
  font-size: 12px;
  color: #6b7280;
  text-decoration: none;
}
.view-events:hover {
  text-decoration: underline;
}
</style>
